<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import KanbanAPI from '../../../../api/kanban';
import Button from '../../../../components-next/button/Button.vue';

const props = defineProps({
  currentFunnel: {
    type: Object,
    required: true,
  },
  funnels: {
    type: Array,
    default: () => [],
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'items-imported']);

const { t } = useI18n();
const store = useStore();

// Função para garantir que o valor é uma string
const ensureString = value => (value ? String(value) : '');

const step = ref('upload'); // 'upload', 'mapping', 'importing'
const file = ref(null);
const fileInput = ref(null);
const csvColumns = ref([]);
const previewRows = ref([]);
const totalRows = ref(0);
const loading = ref(false);
const error = ref(null);

// Computed para obter todos os funis
const funnels = computed(() => props.funnels || []);

// Computed para obter o funil selecionado
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

// Form data para funil e etapa
const importFormData = ref({
  funnel_id: ensureString(props.currentFunnel?.id || ''),
  stage_id: '',
});

// Computed para transformar os funis em options para o select
const funnelOptions = computed(() => {
  const options = funnels.value.map(funnel => ({
    value: String(funnel.id),
    label: funnel.name,
  }));

  // Se o funil atual não está na lista, adiciona ele
  if (importFormData.value.funnel_id) {
    const currentFunnelId = String(importFormData.value.funnel_id);
    const funnelExists = options.some(
      option => option.value === currentFunnelId
    );

    if (!funnelExists && props.currentFunnel) {
      options.unshift({
        value: currentFunnelId,
        label: props.currentFunnel.name || `Funil ${currentFunnelId}`,
      });
    }
  }

  return options;
});

// Computed para obter as etapas do funil selecionado
const funnelStages = computed(() => {
  const currentFunnel = funnels.value.find(
    f => String(f.id) === importFormData.value.funnel_id
  ) || props.currentFunnel;

  let stages = [];

  if (currentFunnel?.stages) {
    stages = Object.entries(currentFunnel.stages)
      .map(([id, stage]) => ({
        value: String(id),
        label: stage.name,
        position: stage.position,
      }))
      .sort((a, b) => a.position - b.position);
  }

  return stages;
});

// Computed para o v-model do funnel_id
const selectedFunnelId = computed({
  get: () => {
    return importFormData.value.funnel_id;
  },
  set: async value => {
    importFormData.value.funnel_id = ensureString(value);
    await handleFunnelChange(value);
  },
});

// Computed para o v-model do stage_id
const selectedStageId = computed({
  get: () => importFormData.value.stage_id,
  set: value => {
    importFormData.value.stage_id = ensureString(value);
  },
});

// Handler para quando um funil é selecionado
const handleFunnelChange = async value => {
  try {
    importFormData.value.funnel_id = ensureString(value);

    // Busca o funil completo dos records do store
    const selectedFunnelData = funnels.value.find(
      f => String(f.id) === importFormData.value.funnel_id
    ) || props.currentFunnel;

    if (selectedFunnelData) {
      // Atualiza o funil selecionado no store
      await store.dispatch('funnel/setSelectedFunnel', selectedFunnelData);

      await nextTick();

      // Verifica se a etapa atual existe no novo funil
      const stages = Object.keys(selectedFunnelData.stages || {});
      const currentStageId = importFormData.value.stage_id;
      const stageExists = stages.some(id => String(id) === currentStageId);

      // Limpa a etapa se não existir no novo funil
      if (!stageExists) {
        importFormData.value.stage_id = stages[0] || '';
      }
    }
  } catch (error) {
    console.error('Erro ao mudar funil:', error);
  }
};

// Campos mapeáveis do KanbanItem
const mappableFields = [
  { value: '', label: '-- Ignorar --' },
  { value: 'item_details.title', label: 'Título (obrigatório)' },
  { value: 'item_details.description', label: 'Descrição' },
  { value: 'item_details.status', label: 'Status' },
  { value: 'item_details.priority', label: 'Prioridade' },
  { value: 'item_details.value', label: 'Valor' },
  { value: 'item_details.customer_name', label: 'Nome do Cliente' },
  { value: 'item_details.customer_email', label: 'Email do Cliente' },
  { value: 'item_details.customer_phone', label: 'Telefone do Cliente' },
  { value: 'item_details.deadline_at', label: 'Data de Vencimento' },
      { value: 'funnel_stage', label: 'Etapa (opcional - use o seletor abaixo se não mapear)' },
  { value: 'assigned_agents', label: 'Agentes (separados por ; ou ,)' },
  { value: 'conversation_display_id', label: 'ID da Conversa' },
];

// Mapeamentos de colunas CSV para campos do KanbanItem
const columnMappings = ref({});

// Estatísticas de importação
const importStats = ref(null);

const handleFileSelect = async (event) => {
  const selectedFile = event.target.files?.[0];
  if (!selectedFile) return;

  if (!selectedFile.name.endsWith('.csv')) {
    error.value = 'Por favor, selecione um arquivo CSV';
    return;
  }

  file.value = selectedFile;
  error.value = null;
  loading.value = true;

  try {
    const response = await KanbanAPI.importPreview(selectedFile);
    csvColumns.value = response.data.columns || [];
    previewRows.value = response.data.preview_rows || [];
    totalRows.value = response.data.total_rows || 0;

    // Inicializar mapeamentos vazios
    columnMappings.value = {};
    csvColumns.value.forEach(column => {
      columnMappings.value[column] = '';
    });

    step.value = 'mapping';
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao processar arquivo CSV';
    console.error('Erro ao processar CSV:', err);
  } finally {
    loading.value = false;
  }
};

const handleFileDrop = (event) => {
  event.preventDefault();
  const droppedFile = event.dataTransfer.files[0];
  if (droppedFile && droppedFile.name.endsWith('.csv')) {
    file.value = droppedFile;
    // Simular seleção de arquivo
    const mockEvent = { target: { files: [droppedFile] } };
    handleFileSelect(mockEvent);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
};

const handleImport = async () => {
  // Validar mapeamentos obrigatórios
  const hasTitle = Object.values(columnMappings.value).includes('item_details.title');
  
  if (!hasTitle) {
    error.value = 'É obrigatório mapear pelo menos o campo "Título"';
    return;
  }

  // Validar funil e etapa
  if (!importFormData.value.funnel_id) {
    error.value = 'É obrigatório selecionar um funil';
    return;
  }

  if (!importFormData.value.stage_id) {
    error.value = 'É obrigatório selecionar uma etapa';
    return;
  }

  loading.value = true;
  error.value = null;
  step.value = 'importing';

  try {
    const response = await KanbanAPI.importItems(
      importFormData.value.funnel_id,
      file.value,
      columnMappings.value,
      importFormData.value.stage_id
    );

    importStats.value = response.data;
    emit('items-imported', response.data);
  } catch (err) {
    error.value = err.response?.data?.error || 'Erro ao importar itens';
    console.error('Erro ao importar:', err);
    step.value = 'mapping'; // Voltar para a etapa de mapeamento
  } finally {
    loading.value = false;
  }
};

const handleReset = () => {
  file.value = null;
  csvColumns.value = [];
  previewRows.value = [];
  columnMappings.value = {};
  error.value = null;
  importStats.value = null;
  step.value = 'upload';
  importFormData.value.stage_id = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Inicializar o funil e etapa ao montar
onMounted(async () => {
  try {
    // Se houver funis, buscar do store
    if (props.funnels?.length === 0) {
      await store.dispatch('funnel/fetch');
    }

    // Inicializar com o funil atual
    if (props.currentFunnel?.id) {
      importFormData.value.funnel_id = ensureString(props.currentFunnel.id);
      
      // Se o funil tem etapas, selecionar a primeira
      const stages = Object.keys(props.currentFunnel.stages || {});
      if (stages.length > 0) {
        importFormData.value.stage_id = stages[0];
      }

      // Atualizar o store
      await store.dispatch('funnel/setSelectedFunnel', props.currentFunnel);
    }
  } catch (error) {
    console.error('Erro ao inicializar formulário de importação:', error);
  }
});

const handleClose = () => {
  handleReset();
  emit('close');
};

const isMappingValid = computed(() => {
  const mappings = Object.values(columnMappings.value);
  const hasTitle = mappings.includes('item_details.title');
  const hasFunnel = !!importFormData.value.funnel_id;
  const hasStage = !!importFormData.value.stage_id;
  return hasTitle && hasFunnel && hasStage;
});

const getFieldLabel = (fieldValue) => {
  const field = mappableFields.find(f => f.value === fieldValue);
  return field ? field.label : fieldValue;
};
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        @click.self="handleClose"
      >
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div class="kanban-import-modal bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[calc(90vh+20px)] overflow-hidden border border-slate-100 dark:border-slate-800">
            <div class="p-6 overflow-y-auto max-h-[calc(90vh-20px)]">
              <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-lg font-medium">Importar Itens do CSV</h3>
        <button
          v-if="step !== 'importing'"
          class="px-3 py-1 text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
          @click="handleClose"
        >
          Fechar
        </button>
      </div>

      <!-- Error Message -->
      <div
        v-if="error"
        class="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm"
      >
        {{ error }}
      </div>

      <!-- Step 1: Upload -->
      <div v-if="step === 'upload'">
        <div
          class="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-woot-500 dark:hover:border-woot-500 transition-colors"
          @drop="handleFileDrop"
          @dragover="handleDragOver"
          @click="fileInput?.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            class="hidden"
            @change="handleFileSelect"
          />
          <div class="flex justify-center mb-4 text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text-icon lucide-file-text">
              <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/>
              <path d="M14 2v5a1 1 0 0 0 1 1h5"/>
              <path d="M10 9H8"/>
              <path d="M16 13H8"/>
              <path d="M16 17H8"/>
            </svg>
          </div>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-2">
            Clique para selecionar ou arraste um arquivo CSV aqui
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-500">
            Formato aceito: .csv
          </p>
        </div>
      </div>

      <!-- Step 2: Mapping -->
      <div v-if="step === 'mapping'">
        <div class="mb-4">
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-2">
            Arquivo: <strong>{{ file?.name }}</strong> ({{ totalRows }} linhas)
          </p>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Mapeie as colunas do CSV para os campos do sistema:
          </p>
        </div>

        <!-- Seletores de Funil e Etapa -->
        <div class="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-4">
            Funil e Etapa de Destino
          </h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                Funil
              </label>
              <select
                v-model="selectedFunnelId"
                class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
              >
                <option value="" disabled>
                  Selecione um funil
                </option>
                <option
                  v-for="option in funnelOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                Etapa
              </label>
              <select
                v-model="selectedStageId"
                class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-700 dark:border-slate-600"
                :disabled="!importFormData.funnel_id"
              >
                <option value="" disabled>
                  Selecione uma etapa
                </option>
                <option
                  v-for="option in funnelStages"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Preview Table -->
        <div class="mb-6 border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
          <table class="w-full text-xs">
            <thead class="bg-slate-50 dark:bg-slate-800 sticky top-0">
              <tr>
                <th
                  v-for="column in csvColumns"
                  :key="column"
                  class="px-3 py-2 text-left font-medium text-slate-700 dark:text-slate-300 border-b"
                >
                  {{ column }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
              <tr
                v-for="(row, index) in previewRows"
                :key="index"
                class="hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <td
                  v-for="column in csvColumns"
                  :key="column"
                  class="px-3 py-2 text-slate-600 dark:text-slate-400"
                >
                  {{ row[column] || '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Column Mappings -->
        <div class="space-y-3 mb-6">
          <div
            v-for="column in csvColumns"
            :key="column"
            class="flex items-center gap-3"
          >
            <label class="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[150px]">
              {{ column }}:
            </label>
            <select
              v-model="columnMappings[column]"
              class="flex-1 p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm"
            >
              <option
                v-for="field in mappableFields"
                :key="field.value"
                :value="field.value"
              >
                {{ field.label }}
              </option>
            </select>
            <span
              v-if="columnMappings[column]"
              class="text-xs text-slate-500 dark:text-slate-400"
            >
              → {{ getFieldLabel(columnMappings[column]) }}
            </span>
          </div>
        </div>

        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="handleReset"
          >
            Voltar
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :isLoading="loading"
            :disabled="!isMappingValid"
            @click="handleImport"
          >
            Importar {{ totalRows }} itens
          </Button>
        </div>
      </div>

      <!-- Step 3: Importing/Results -->
      <div v-if="step === 'importing'">
        <div v-if="loading" class="text-center py-8">
          <div class="inline-block mb-4 animate-spin">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-history-icon lucide-history text-slate-600 dark:text-slate-400">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
              <path d="M12 7v5l4 2"/>
            </svg>
          </div>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            Importando itens...
          </p>
        </div>

        <div v-else-if="importStats" class="space-y-4">
          <div class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 class="font-medium text-green-800 dark:text-green-300 mb-2">
              Importação concluída!
            </h4>
            <div class="text-sm text-green-700 dark:text-green-400 space-y-1">
              <p>✅ {{ importStats.created_count }} itens importados com sucesso</p>
              <p v-if="importStats.error_count > 0">
                ⚠️ {{ importStats.error_count }} itens com erro
              </p>
              <p>Total processado: {{ importStats.total_rows }} linhas</p>
            </div>
          </div>

          <!-- Errors List -->
          <div
            v-if="importStats.errors && importStats.errors.length > 0"
            class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-h-64 overflow-y-auto"
          >
            <h4 class="font-medium text-red-800 dark:text-red-300 mb-2">
              Erros encontrados:
            </h4>
            <ul class="text-xs text-red-700 dark:text-red-400 space-y-1">
              <li
                v-for="(err, index) in importStats.errors"
                :key="index"
              >
                Linha {{ err.row }}: {{ err.error }}
              </li>
            </ul>
          </div>

          <div class="flex justify-end gap-2">
            <Button
              variant="solid"
              color="blue"
              size="sm"
              @click="handleClose"
            >
              Fechar
            </Button>
          </div>
        </div>
      </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.kanban-import-modal {
  width: 100%;
  max-width: 800px;
}
</style>
